import React, { useEffect, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
} from "react-router-dom";
import AuthLayout from "./layouts/AuthLayout";
import PublicLayout from "./layouts/PublicLayout";
import MetaPixelRouteTracker from "./components/MetaPixelRouteTracker";
import { AuthProvider } from "./features/auth/hooks/useAuth";

import {
  ACCOUNT_ROUTE,
  ADMIN_ROUTE,
  ADMIN_USERS_ROUTE,
  ADMIN_EVENTS_ROUTE,
  ADMIN_EVENT_EDIT_ROUTE,
  ADMIN_SUBSCRIPTIONS_ROUTE,
  ADMIN_ORDERS_ROUTE,
  ADMIN_PLANS_ROUTE,
  ADMIN_PLAN_EDIT_ROUTE,
  ADMIN_WITHDRAWALS_ROUTE,
  ADMIN_CRM_ROUTE,
  ADMIN_PROMOS_ROUTE,
  ADMIN_CHATS_ROUTE,
  ADMIN_CHAT_SETTINGS_ROUTE,
  ADMIN_SETTINGS_ROUTE,
  ADMIN_CHAT_REPORTS_ROUTE,
  ADMIN_BLOCKED_USERS_ROUTE,
  ADMIN_FEES_ROUTE,
  ADMIN_ADVERTISING_ROUTE,
  ADMIN_QUOTES_ROUTE,
  AdminRoute,
  CALENDAR_ROUTE,
  CHECKOUT_ROUTE,
  CONFIRM_MAIL_ROUTE,
  REENGAGEMENT_ROUTE,
  CHATS_ROUTE,
  COOKIE_POLICY_ROUTE,
  CREATE_EVENT_ROUTE,
  EVENTS_ROUTE,
  DOWNLOAD_APP_ROUTE,
  GALLERY_ROUTE,
  FAQ_ROUTE,
  FORGOT_PWD_ROUTE,
  LANDING_ROUTE,
  LOGIN_ROUTE,
  MY_EVENTS_ROUTE,
  PAYMENT_METHODS_ROUTE,
  PASSWORD_RECOVERY_ROUTE,
  PRIVACY_POLICY_ROUTE,
  PROFILE_ROUTE,
  RELIABILITY_HISTORY_ROUTE,
  ProtectedRoute,
  SIGNOUT_ROUTE,
  SIGNUP_ROUTE,
  TERMS_ROUTE,
  EVENT_APPROVED_INVITE_ROUTE,
  UPDATE_EVENT_ROUTE,
  EVENT_ATTENDANCE_ROUTE,
  UPDATE_SUBSCRIPTION_ROUTE,
  WELCOME_ROUTE,
  ONBOARDING_ROUTE,
  ONBOARDING_RESULT_ROUTE,
  SUBSCRIPTIONS_PLANS_ROUTE,
  SUBSCRIPTION_ACTIVATION_ROUTE,
  GIOJOY_NEWS_ROUTE,
  AD_EXAMPLES_ROUTE,
  EXTRA_ROUTE,
  FOLLOWERS_ROUTE,
  USERS_MET_ROUTE,
  CHAT_INVITE_ROUTE,
} from "./routes";

import { ToastProvider } from "./context/ToastContext";
import { CookieConsentBanner } from "./pages/CookieConsentBanner";
import { IOSInstallationBanner } from "./components/iOSInstallationBanner";
import { AttendanceRequestBanner } from "./features/events/components/AttendanceRequestBanner";
import { FirebaseNotificationWrapper } from "./components/firebaseNotificationWrapper";
import RootRedirect from "./components/RootRedirect";
import { WebSocketProvider } from "./features/chat/context/WebSocketProvider";
import { FileUploadProvider } from "./hooks/FileUploadContext";
import { ActiveChatProvider } from "./context/ActiveChatContext";
import { AppStateManager } from "./components/AppStateManager";
import ProfilePhotoFirstLoginNudge from "./features/user/components/ProfilePhotoFirstLoginNudge";
import { ApolloError } from "@apollo/client";
import tokenService from "./services/tokenService";
import AdminLayout from "./features/admin/components/AdminLayout";
import Page404 from "./pages/404";
import { AdProvider } from "./components/Ads";
import AdExamples from "./components/Ads/AdExamples";
import { useAcquisitionTracking } from "./hooks/useAcquisitionTracking";
import { AdBannerProvider } from "./context/AdBannerContext";

// Async load page components for bundle size optimization
const SignIn = lazy(() => import("./features/auth/pages/SignIn"));
const Signup = lazy(() => import("./features/auth/pages/Signup"));
const NewEvent = lazy(() => import("./features/events/pages/NewEvent"));
const Event = lazy(() => import("./features/events/pages/Event"));
const EventApprovedInvite = lazy(() => import("./features/events/pages/EventApprovedInvite"));
const EventAttendance = lazy(() => import("./features/events/pages/EventAttendance"));
const UpdateSubscriptionPage = lazy(() => import("./features/events/pages/UpdateSubscriptionPage"));
const Calendar = lazy(() => import("./features/events/pages/Calendar"));
const EventList = lazy(() => import("./features/events/pages/EventList"));
const Welcome = lazy(() => import("./features/auth/pages/Welcome"));
const Logout = lazy(() => import("./features/auth/pages/Logout"));
const Profile = lazy(() => import("./features/user/pages/Profile"));
const ReliabilityHistory = lazy(() => import("./features/user/pages/ReliabilityHistory"));
const Followers = lazy(() => import("./features/user/pages/Followers"));
const UsersMet = lazy(() => import("./features/user/pages/UsersMet"));
const Checkout = lazy(() => import("./features/events/pages/Checkout"));
const ConfirmEmail = lazy(() => import("./features/public/pages/ConfirmEmail"));
const ActivateReengagementBonus = lazy(() => import("./features/user/pages/ActivateReengagementBonus"));
const CookiePolicy = lazy(() => import("./features/public/pages/CookiePolicy"));
const PrivacyPolicy = lazy(() => import("./features/public/pages/PrivacyPolicy"));
const PasswordRecovery = lazy(() => import("./features/auth/pages/PasswordReset"));
const ForgotPassword = lazy(() => import("./features/auth/pages/ForgotPassoword"));
const MyEvents = lazy(() => import("./features/events/pages/MyEvents"));
const ChatLayout = lazy(() => import("./features/chat/pages/ChatLayout"));
const AcceptChatInvitation = lazy(() => import("./features/chat/pages/AcceptChatInvitation"));
const FAQ = lazy(() => import("./features/public/pages/FAQ"));
const TermsOfUse = lazy(() => import("./features/public/pages/Terms"));
const Account = lazy(() => import("./features/user/pages/Account"));
const Onboarding = lazy(() => import("./features/seller/pages/OnBoarding"));
const OnboardingResult = lazy(() => import("./features/seller/pages/OnBoardingResult"));
const Plans = lazy(() => import("./features/subscription/pages/Plans"));
const PaymentMethods = lazy(() => import("./features/subscription/pages/PaymentMethods"));
const SubscriptionActivation = lazy(() => import("./features/subscription/pages/SubscriptionActivation"));
const AdminDashboard = lazy(() => import("./features/admin/pages/AdminDashboard"));
const UsersManagement = lazy(() => import("./features/admin/pages/UsersManagement"));
const EventsManagement = lazy(() => import("./features/admin/pages/EventsManagement"));
const AdminEventEdit = lazy(() => import("./features/admin/pages/AdminEventEdit"));
const SubscriptionsManagement = lazy(() => import("./features/admin/pages/SubscriptionsManagement"));
const OrdersManagement = lazy(() => import("./features/admin/pages/OrdersManagement"));
const ChatsManagement = lazy(() => import("./features/admin/pages/ChatsManagement"));
const FeesManagement = lazy(() => import("./features/admin/pages/FeesManagement"));
const AdminPlans = lazy(() => import("./features/admin/pages/AdminPlans"));
const AdminPlanEdit = lazy(() => import("./features/admin/pages/AdminPlanEdit"));
const WithdrawalsManagement = lazy(() => import("./features/admin/pages/WithdrawalsManagement"));
const AdvertisingManagement = lazy(() => import("./features/admin/pages/AdvertisingManagement"));
const QuotesManagement = lazy(() => import("./features/admin/pages/QuotesManagement"));
const ChatAssistantSettings = lazy(() => import("./features/admin/pages/ChatAssistantSettings"));
const PlatformSettingsManagement = lazy(() => import("./features/admin/pages/PlatformSettingsManagement"));
const MiniCrm = lazy(() => import("./features/admin/pages/MiniCrm"));
const CampaignEditorPage = lazy(() => import("./features/admin/pages/CampaignEditorPage"));
const PromoManagement = lazy(() => import("./features/admin/pages/PromoManagement"));
const ChatReportsManagement = lazy(() => import("./features/admin/pages/ChatReportsManagement"));
const BlockedUsersManagement = lazy(() => import("./features/admin/pages/BlockedUsersManagement"));
const GioJoyNews = lazy(() => import("./pages/GioJoyNews"));
const LandingDM = lazy(() => import("./features/public/pages/LandingDigitalMarketing"));
const DownloadApp = lazy(() => import("./features/public/pages/DownloadApp"));
const EventGalleryTeaser = lazy(
  () => import("./features/gallery/pages/EventGalleryTeaser"),
);
const ExtraPage = lazy(() => import("./pages/ExtraPage"));


// Test page component for AdSense examples
const AdExamplesTestPage: React.FC = () => {
  const [showAds, setShowAds] = React.useState(true);

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">AdSense Examples</h1>
      <div className="mb-6 p-4 bg-blue-50 rounded-lg">
        <p className="text-blue-800 mb-4">
          This is a test page to demonstrate the AdSense integration. Toggle the
          button below to see how ads appear/disappear.
        </p>
        <button
          onClick={() => setShowAds(!showAds)}
          className={`px-4 py-2 rounded font-medium ${
            showAds
              ? "bg-red-500 hover:bg-red-600 text-white"
              : "bg-green-500 hover:bg-green-600 text-white"
          }`}
        >
          {showAds ? "Hide Ads (Premium Mode)" : "Show Ads (Free Mode)"}
        </button>
      </div>
      <AdExamples showAds={showAds} />
    </div>
  );
};

const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
      <p className="mt-4 text-gray-600">Caricamento...</p>
    </div>
  </div>
);

const App: React.FC = () => {
  // Initialize acquisition tracking globally (captures UTM params on ANY page load)
  useAcquisitionTracking();

  // Simplified service worker message handling
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", async (event) => {
        console.log("📨 SW Message:", event.data?.type);

        if (event.data?.type === "sw-log") {
          console.log("[SW]", event.data.msg);
        }

        // Handle simple auth state check request from service worker
        if (event.data?.type === "check-auth-state") {
          console.log("🔐 Service worker requesting auth state check");
          // Trigger auth validation by dispatching a custom event
          window.dispatchEvent(new CustomEvent("sw-auth-check"));
        }

        // Handle navigation request from service worker
        if (event.data?.type === "navigate" && event.data.url) {
          console.log(
            "🧭 Service worker requesting navigation to:",
            event.data.url,
          );
          const targetPath = event.data.url.replace(window.location.origin, "");
          if (targetPath !== window.location.pathname) {
            window.location.href = event.data.url;
          }
        }
      });
    }
  }, []);

  useEffect(() => {
    // Handle auth errors from GraphQL operations
    const handleAuthError = async (event: any) => {
      if (window.__ignoreAuthErrors) {
        console.log("Auth error ignored during login grace period");
        return;
      }

      console.log("Auth error detected:", event.detail?.message);

      // Prevent multiple redirects
      if (window.location.pathname === "/login") {
        console.log("Already on login page, not redirecting again");
        return;
      }

      try {
        // Try to refresh the token before redirecting
        const tokens = await tokenService.getTokens();
        const refreshToken = await tokenService.getRefreshToken();

        if (refreshToken && tokens) {
          console.log("Attempting token refresh before redirecting...");

          // Dispatch a custom event to trigger token refresh in AuthProvider
          window.dispatchEvent(
            new CustomEvent("force-token-refresh", {
              detail: { refreshToken },
            }),
          );

          // Give the refresh a chance to complete before potentially redirecting
          return;
        }
      } catch (e) {
        console.error("Error attempting refresh before redirect:", e);
      }

      // Use a small delay to prevent React state update conflicts
      setTimeout(() => {
        window.location.href = "/login?error=session_expired";
      }, 300);
    };

    // Register global error handler
    window.addEventListener("auth-error", handleAuthError);

    // Handle uncaught Apollo errors
    const handleUncaughtErrors = (event: ErrorEvent) => {
      if (
        event.error instanceof ApolloError ||
        (event.error &&
          event.error.message &&
          (event.error.message.includes("Apollo") ||
            event.error.message.includes("GraphQL")))
      ) {
        console.error("Caught Apollo error:", event.error);

        // If it's an auth error, handle appropriately
        if (
          event.error.message.includes("UNAUTHORIZED") ||
          event.error.message.includes("401")
        ) {
          window.dispatchEvent(
            new CustomEvent("auth-error", {
              detail: {
                message: "Session expired due to authentication error.",
              },
            }),
          );
        }
      }
    };

    window.addEventListener("error", handleUncaughtErrors);

    return () => {
      window.removeEventListener("auth-error", handleAuthError);
      window.removeEventListener("error", handleUncaughtErrors);
    };
  }, []);

  return (
    <AuthProvider>
      <AppStateManager />
      <ToastProvider>
        <AdProvider>
            <AdBannerProvider>
              <WebSocketProvider>
                <FileUploadProvider>
                  <Router>
                    <ActiveChatProvider>
                      <MetaPixelRouteTracker />
                      <IOSInstallationBanner />
                      <Suspense fallback={<LoadingFallback />}>
                      <Routes>
                        <Route
                          path={LANDING_ROUTE}
                          element={
                            <PublicLayout>
                              <LandingDM />
                            </PublicLayout>
                          }
                        />

                        <Route
                          path={DOWNLOAD_APP_ROUTE}
                          element={
                            <PublicLayout>
                              <DownloadApp />
                            </PublicLayout>
                          }
                        />

                        {/* Landing page for the gallery_photo_added push.
                            Public so a shared link shows the event and the
                            download CTA instead of a login wall. */}
                        <Route
                          path={GALLERY_ROUTE}
                          element={
                            <PublicLayout>
                              <EventGalleryTeaser />
                            </PublicLayout>
                          }
                        />

                        <Route
                          path={WELCOME_ROUTE}
                          element={
                            <PublicLayout>
                              <Welcome />
                            </PublicLayout>
                          }
                        />
                        <Route
                          path={COOKIE_POLICY_ROUTE}
                          element={
                            <PublicLayout>
                              <CookiePolicy />
                            </PublicLayout>
                          }
                        />
                        <Route
                          path={FAQ_ROUTE}
                          element={
                            <PublicLayout>
                              <FAQ />
                            </PublicLayout>
                          }
                        />
                        <Route
                          path={PRIVACY_POLICY_ROUTE}
                          element={
                            <PublicLayout>
                              <PrivacyPolicy />
                            </PublicLayout>
                          }
                        />

                        <Route
                          path={ACCOUNT_ROUTE}
                          element={
                            <ProtectedRoute
                              component={() => (
                                <PublicLayout>
                                  <Account />
                                </PublicLayout>
                              )}
                            />
                          }
                        />

                        <Route
                          path={TERMS_ROUTE}
                          element={
                            <PublicLayout>
                              <TermsOfUse />
                            </PublicLayout>
                          }
                        />

                        <Route
                          path={GIOJOY_NEWS_ROUTE}
                          element={
                            <ProtectedRoute
                              component={() => (
                                <PublicLayout>
                                  <GioJoyNews />
                                </PublicLayout>
                              )}
                            />
                          }
                        />

                        <Route
                          path={EXTRA_ROUTE}
                          element={
                            <ProtectedRoute
                              component={() => (
                                <PublicLayout>
                                  <ExtraPage />
                                </PublicLayout>
                              )}
                            />
                          }
                        />

                        {/* Test route for AdSense examples */}
                        <Route
                          path={AD_EXAMPLES_ROUTE}
                          element={
                            <AdminRoute
                              component={() => (
                                <PublicLayout>
                                  <AdExamplesTestPage />
                                </PublicLayout>
                              )}
                            />
                          }
                        />

                        <Route
                          path={CONFIRM_MAIL_ROUTE}
                          element={
                            <PublicLayout>
                              <ConfirmEmail />
                            </PublicLayout>
                          }
                        />

                        <Route
                          path={REENGAGEMENT_ROUTE}
                          element={
                            <PublicLayout>
                              <ActivateReengagementBonus />
                            </PublicLayout>
                          }
                        />
                        <Route path={SIGNOUT_ROUTE} element={<Logout />} />

                        <Route
                          path={LOGIN_ROUTE}
                          element={
                            <AuthLayout>
                              <SignIn />
                            </AuthLayout>
                          }
                        />
                        <Route
                          path={SIGNUP_ROUTE}
                          element={
                            <AuthLayout>
                              <Signup />
                            </AuthLayout>
                          }
                        />

                        <Route
                          path={FORGOT_PWD_ROUTE}
                          element={
                            <AuthLayout>
                              <ForgotPassword />
                            </AuthLayout>
                          }
                        />
                        <Route
                          path={PASSWORD_RECOVERY_ROUTE}
                          element={
                            <AuthLayout>
                              <PasswordRecovery />
                            </AuthLayout>
                          }
                        />

                        <Route
                          path={EVENTS_ROUTE}
                          element={
                            <PublicLayout>
                              <EventList />
                            </PublicLayout>
                          }
                        />
                        
                        <Route
                          path={EVENT_APPROVED_INVITE_ROUTE}
                          element={
                            <ProtectedRoute
                              component={() => <EventApprovedInvite />}
                            />
                          }
                        />

                        <Route
                          path={EVENT_ATTENDANCE_ROUTE}
                          element={
                            <ProtectedRoute
                              component={() => <EventAttendance />}
                            />
                          }
                        />

                       <Route
                          path={`${EVENTS_ROUTE}/:eventId`}
                          element={
                            <PublicLayout>
                              <Event />
                            </PublicLayout>
                          }
                        />

                        <Route
                          path={UPDATE_SUBSCRIPTION_ROUTE}
                          element={
                            <ProtectedRoute
                              component={() => <UpdateSubscriptionPage />}
                            />
                          }
                        />

                        <Route
                          path={CALENDAR_ROUTE}
                          element={
                            <PublicLayout>
                              <Calendar />
                            </PublicLayout>
                          }
                        />

                        <Route
                          path={SUBSCRIPTIONS_PLANS_ROUTE}
                          element={
                            <PublicLayout>
                              <Plans />
                            </PublicLayout>
                          }
                        />

                        <Route
                          path={SUBSCRIPTION_ACTIVATION_ROUTE}
                          element={
                            <PublicLayout>
                              <SubscriptionActivation />
                            </PublicLayout>
                          }
                        />

                        <Route
                          path={PAYMENT_METHODS_ROUTE}
                          element={
                            <ProtectedRoute
                              component={() => (
                                <PublicLayout>
                                  <PaymentMethods />
                                </PublicLayout>
                              )}
                            />
                          }
                        />

                        <Route
                          path={CREATE_EVENT_ROUTE}
                          element={
                            <ProtectedRoute
                              component={() => (
                                <PublicLayout>
                                  <NewEvent />
                                </PublicLayout>
                              )}
                            />
                          }
                        />
                        <Route
                          path={UPDATE_EVENT_ROUTE}
                          element={
                            <ProtectedRoute
                              component={() => (
                                <PublicLayout>
                                  <NewEvent />
                                </PublicLayout>
                              )}
                            />
                          }
                        />

                        <Route
                          path={PROFILE_ROUTE}
                          element={
                            <ProtectedRoute
                              component={() => (
                                <PublicLayout>
                                  <Profile />
                                </PublicLayout>
                              )}
                            />
                          }
                        />

                        <Route
                          path={RELIABILITY_HISTORY_ROUTE}
                          element={
                            <ProtectedRoute
                              component={() => (
                                <PublicLayout>
                                  <ReliabilityHistory />
                                </PublicLayout>
                              )}
                            />
                          }
                        />

                        <Route
                          path={FOLLOWERS_ROUTE}
                          element={
                            <ProtectedRoute
                              component={() => (
                                <PublicLayout>
                                  <Followers />
                                </PublicLayout>
                              )}
                            />
                          }
                        />

                        <Route
                          path={USERS_MET_ROUTE}
                          element={
                            <ProtectedRoute
                              component={() => (
                                <PublicLayout>
                                  <UsersMet />
                                </PublicLayout>
                              )}
                            />
                          }
                        />

                        <Route
                          path={MY_EVENTS_ROUTE}
                          element={
                            <ProtectedRoute
                              component={() => (
                                <PublicLayout>
                                  <MyEvents />
                                </PublicLayout>
                              )}
                            />
                          }
                        />
                        <Route
                          path={CHATS_ROUTE}
                          element={
                            <ProtectedRoute
                              component={() => (
                                <PublicLayout>
                                  <ChatLayout />
                                </PublicLayout>
                              )}
                            />
                          }
                        />
                        <Route
                          path={CHAT_INVITE_ROUTE}
                          element={
                            <ProtectedRoute
                              component={() => (
                                <PublicLayout>
                                  <AcceptChatInvitation />
                                </PublicLayout>
                              )}
                            />
                          }
                        />

                        <Route
                          path={`${CHECKOUT_ROUTE}/:eventId`}
                          element={
                            <ProtectedRoute
                              component={() => (
                                <PublicLayout>
                                  <Checkout />
                                </PublicLayout>
                              )}
                            />
                          }
                        />

                        <Route
                          path={ONBOARDING_ROUTE}
                          element={
                            <ProtectedRoute
                              component={() => (
                                <PublicLayout>
                                  <Onboarding />
                                </PublicLayout>
                              )}
                            />
                          }
                        />

                        <Route
                          path={ONBOARDING_RESULT_ROUTE}
                          element={
                            <ProtectedRoute
                              component={() => (
                                <PublicLayout>
                                  <OnboardingResult />
                                </PublicLayout>
                              )}
                            />
                          }
                        />

                        {/* Admin Routes */}
                        <Route
                          path={ADMIN_ROUTE}
                          element={
                            <AdminRoute
                              component={() => (
                                <AdminLayout>
                                  <AdminDashboard />
                                </AdminLayout>
                              )}
                            />
                          }
                        />
                        <Route
                          path={ADMIN_USERS_ROUTE}
                          element={
                            <AdminRoute
                              component={() => (
                                <AdminLayout>
                                  <UsersManagement />
                                </AdminLayout>
                              )}
                            />
                          }
                        />
                        <Route
                          path={ADMIN_EVENTS_ROUTE}
                          element={
                            <AdminRoute
                              component={() => (
                                <AdminLayout>
                                  <EventsManagement />
                                </AdminLayout>
                              )}
                            />
                          }
                        />
                        <Route
                          path={ADMIN_EVENT_EDIT_ROUTE}
                          element={
                            <AdminRoute
                              component={() => (
                                <AdminLayout>
                                  <AdminEventEdit />
                                </AdminLayout>
                              )}
                            />
                          }
                        />
                        <Route
                          path={ADMIN_SUBSCRIPTIONS_ROUTE}
                          element={
                            <AdminRoute
                              component={() => (
                                <AdminLayout>
                                  <SubscriptionsManagement />
                                </AdminLayout>
                              )}
                            />
                          }
                        />
                        <Route
                          path={ADMIN_ORDERS_ROUTE}
                          element={
                            <AdminRoute
                              component={() => (
                                <AdminLayout>
                                  <OrdersManagement />
                                </AdminLayout>
                              )}
                            />
                          }
                        />
                        <Route
                          path={ADMIN_PLANS_ROUTE}
                          element={
                            <AdminRoute
                              component={() => (
                                <AdminLayout>
                                  <AdminPlans />
                                </AdminLayout>
                              )}
                            />
                          }
                        />
                        <Route
                          path={ADMIN_PLAN_EDIT_ROUTE}
                          element={
                            <AdminRoute
                              component={() => (
                                <AdminLayout>
                                  <AdminPlanEdit />
                                </AdminLayout>
                              )}
                            />
                          }
                        />
                        <Route
                          path={ADMIN_CHATS_ROUTE}
                          element={
                            <AdminRoute
                              component={() => (
                                <AdminLayout>
                                  <ChatsManagement />
                                </AdminLayout>
                              )}
                            />
                          }
                        />
                        <Route
                          path={ADMIN_FEES_ROUTE}
                          element={
                            <AdminRoute
                              component={() => (
                                <AdminLayout>
                                  <FeesManagement />
                                </AdminLayout>
                              )}
                            />
                          }
                        />

                        <Route
                          path={ADMIN_WITHDRAWALS_ROUTE}
                          element={
                            <AdminRoute
                              component={() => (
                                <AdminLayout>
                                  <WithdrawalsManagement />
                                </AdminLayout>
                              )}
                            />
                          }
                        />

                        <Route
                          path={ADMIN_ADVERTISING_ROUTE}
                          element={
                            <AdminRoute
                              component={() => (
                                <AdminLayout>
                                  <AdvertisingManagement />
                                </AdminLayout>
                              )}
                            />
                          }
                        />

                        <Route
                          path={ADMIN_QUOTES_ROUTE}
                          element={
                            <AdminRoute
                              component={() => (
                                <AdminLayout>
                                  <QuotesManagement />
                                </AdminLayout>
                              )}
                            />
                          }
                        />

                        <Route
                          path={ADMIN_CHAT_SETTINGS_ROUTE}
                          element={
                            <AdminRoute
                              component={() => (
                                <AdminLayout>
                                  <ChatAssistantSettings />
                                </AdminLayout>
                              )}
                            />
                          }
                        />

                        <Route
                          path={ADMIN_SETTINGS_ROUTE}
                          element={
                            <AdminRoute
                              component={() => (
                                <AdminLayout>
                                  <PlatformSettingsManagement />
                                </AdminLayout>
                              )}
                            />
                          }
                        />

                        <Route
                          path={ADMIN_CRM_ROUTE}
                          element={
                            <AdminRoute
                              component={() => (
                                <AdminLayout>
                                  <MiniCrm />
                                </AdminLayout>
                              )}
                            />
                          }
                        />

                        <Route
                          path={ADMIN_PROMOS_ROUTE}
                          element={
                            <AdminRoute
                              component={() => (
                                <AdminLayout>
                                  <PromoManagement />
                                </AdminLayout>
                              )}
                            />
                          }
                        />
                        <Route
                          path={"/admin/crm/new"}
                          element={
                            <AdminRoute
                              component={() => (
                                <AdminLayout>
                                  <CampaignEditorPage />
                                </AdminLayout>
                              )}
                            />
                          }
                        />
                        <Route
                          path={"/admin/crm/:campaignId/edit"}
                          element={
                            <AdminRoute
                              component={() => (
                                <AdminLayout>
                                  <CampaignEditorPage />
                                </AdminLayout>
                              )}
                            />
                          }
                        />

                        <Route
                          path={ADMIN_CHAT_REPORTS_ROUTE}
                          element={
                            <AdminRoute
                              component={() => (
                                <AdminLayout>
                                  <ChatReportsManagement />
                                </AdminLayout>
                              )}
                            />
                          }
                        />
                        <Route
                          path={ADMIN_BLOCKED_USERS_ROUTE}
                          element={
                            <AdminRoute
                              component={() => (
                                <AdminLayout>
                                  <BlockedUsersManagement />
                                </AdminLayout>
                              )}
                            />
                          }
                        />

                        <Route path="/" element={<RootRedirect />} />

                        {/* 404 Not Found Route */}
                        <Route
                          path="*"
                          element={
                            <PublicLayout>
                              <Page404 />
                            </PublicLayout>
                          }
                        />
                      </Routes>
                    </Suspense>
                    {/* Cookie Consent Banner */}
                    <CookieConsentBanner />
                    <AttendanceRequestBanner />

                    <IOSInstallationBanner />
                    <FirebaseNotificationWrapper />
                    <ProfilePhotoFirstLoginNudge />
                  </ActiveChatProvider>
                </Router>
              </FileUploadProvider>
            </WebSocketProvider>
          </AdBannerProvider>
        </AdProvider>
      </ToastProvider>
    </AuthProvider>
  );
};

export default App;
